import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
} from 'recharts';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

interface WaterfallItem {
    name: string;
    value: number;
    base: number;
    fill: string;
}

export const ProfitWaterfallChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo((): WaterfallItem[] => {
        if (!finance?.metrics) return [];

        const metrics = finance.metrics;
        const grossSales = metrics.grossSales ?? 0;
        const adSpend = metrics.adSpend ?? 0;
        const shippingRevenue = metrics.shippingRevenue ?? 0;
        const transactionFees = metrics.transactionFees ?? 0;
        const discounts = metrics.discounts ?? 0;
        const returns = metrics.returns ?? 0;

        // Journal expenses: total expenses minus the known categories
        const totalExpenses = finance.totalExpenses ?? 0;
        const knownExpenses = adSpend + transactionFees;
        const journalExpenses = Math.max(0, totalExpenses - knownExpenses);

        const steps: { label: string; amount: number; isSubtraction: boolean }[] = [
            { label: t('charts.grossSales', 'Gross Sales'), amount: grossSales, isSubtraction: false },
            { label: t('charts.adSpend', 'Ad Spend'), amount: adSpend, isSubtraction: true },
            { label: t('charts.shipping', 'Shipping'), amount: shippingRevenue, isSubtraction: true },
            { label: t('charts.fees', 'Fees'), amount: transactionFees, isSubtraction: true },
            { label: t('charts.discounts', 'Discounts'), amount: discounts, isSubtraction: true },
            { label: t('charts.returns', 'Returns'), amount: returns, isSubtraction: true },
            { label: t('charts.journalExpenses', 'Other Costs'), amount: journalExpenses, isSubtraction: true },
        ];

        let running = 0;
        const items: WaterfallItem[] = [];

        for (const step of steps) {
            if (step.isSubtraction) {
                const newRunning = running - step.amount;
                items.push({
                    name: step.label,
                    base: Math.min(running, newRunning),
                    value: Math.abs(step.amount),
                    fill: CHART_COLORS.expense,
                });
                running = newRunning;
            } else {
                items.push({
                    name: step.label,
                    base: 0,
                    value: step.amount,
                    fill: CHART_COLORS.revenue,
                });
                running = step.amount;
            }
        }

        // Final net profit bar
        const netProfit = running;
        items.push({
            name: t('charts.netProfit', 'Net Profit'),
            base: 0,
            value: Math.abs(netProfit),
            fill: netProfit >= 0 ? CHART_COLORS.revenue : CHART_COLORS.expense,
        });

        return items;
    }, [finance?.metrics, finance?.totalExpenses, currencySymbol, t]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    if (isEmpty) return null;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                <CartesianGrid
                    vertical={false}
                    stroke={CHART_STYLES.grid.stroke}
                    strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                    strokeWidth={CHART_STYLES.grid.strokeWidth}
                    opacity={CHART_STYLES.grid.opacity}
                />
                <XAxis
                    dataKey="name"
                    {...CHART_STYLES.axis}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                />
                <YAxis
                    tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                    {...CHART_STYLES.axis}
                />
                <Tooltip
                    contentStyle={CHART_STYLES.tooltip.contentStyle}
                    cursor={CHART_STYLES.tooltip.cursor}
                    itemStyle={CHART_STYLES.tooltip.itemStyle}
                    formatter={(value: number) => [formatCurrency(value, currencySymbol)]}
                    labelFormatter={(label: string) => label}
                />
                {/* Invisible base bar to offset the visible bar */}
                <Bar dataKey="base" stackId="waterfall" fill="transparent" />
                <Bar
                    dataKey="value"
                    stackId="waterfall"
                    radius={CHART_STYLES.bar.radius}
                    maxBarSize={CHART_STYLES.bar.maxBarSize}
                >
                    {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                </Bar>
            </ComposedChart>
        </ResponsiveContainer>
    );
};
